import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import newslitLogo from '../../../assets/images/newslit-logo.svg';
import rocketIllustration from '../../../assets/images/rocket-illustration.png';
import ErrorReporter from '../../../components/error-reporter/ErrorReporter';
import LoadingSpinner from '../../../components/loading-spinner/LoadingSpinner';
import { useBriefUnsubscribe } from '../../../hooks/useBriefUnsubscribe';
import DefaultPageLayout from '../../../layouts/defaultPageLayout';

const BriefUnsubscribePage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();

  const [code, setCode] = useState<string | null>(null);
  const [memberId, setMemberId] = useState<string | null>(null);

  const { briefUnsubscribeLink, unsubscribe } = useBriefUnsubscribe(memberId, code);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const verificationCode = searchParams.get('c');
    const member = searchParams.get('m');

    if (!verificationCode || !member) {
      history.push('/');
    } else {
      setCode(verificationCode);
      setMemberId(member);
    }
  }, [location.search, history, setCode, setMemberId]);

  const loadingStage = () => !briefUnsubscribeLink || briefUnsubscribeLink.isFetching;

  const loadedStage = () =>
    !loadingStage() &&
    briefUnsubscribeLink.briefName &&
    !briefUnsubscribeLink.isUnsubscribe &&
    !briefUnsubscribeLink.isCompleted;

  const errorStage = () =>
    !loadingStage() && briefUnsubscribeLink.isFetching === false && !briefUnsubscribeLink.briefName;

  const completedStage = () =>
    !loadingStage() && briefUnsubscribeLink && briefUnsubscribeLink.isCompleted;

  const alreadyUnsubscribedStage = () => !loadingStage() && briefUnsubscribeLink.isUnsubscribe;

  const currentTemplateStage = () => {
    if (loadingStage()) {
      return <LoadingSpinner />;
    }

    if (loadedStage()) {
      return (
        <>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Please Confirm</h2>
          <p className="mt-2 text-sm text-gray-600">
            Unsubscribing from "{briefUnsubscribeLink?.briefName}" Brief. The News Brief will stay
            active but you won't receive any more emails about it.
          </p>
          <button
            type="button"
            className="order-0 w-full flex justify-center mt-5 px-4 py-2 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-secondary hover:bg-secondary-dark hover:text-white hover:no-underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
            disabled={!briefUnsubscribeLink.briefName || briefUnsubscribeLink.isUnsubscribing}
            onClick={() => unsubscribe()}
          >
            {briefUnsubscribeLink.isUnsubscribing ? 'Unsubscribing...' : 'Unsubscribe'}
          </button>
        </>
      );
    }

    if (errorStage()) {
      return (
        <>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Oops! Something went wrong.
          </h2>
          <button
            type="button"
            className="order-0 w-full flex justify-center mt-5 px-4 py-2 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-secondary hover:bg-secondary-dark hover:text-white hover:no-underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
            onClick={() => history.push('/')}
          >
            Return to Newslit
          </button>
        </>
      );
    }

    if (completedStage()) {
      return (
        <>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Unsubscribe Successful</h2>
          <p className="mt-2 text-sm text-gray-600">
            You will no longer receive email from this Brief.
          </p>
          <button
            type="button"
            className="order0 w-full flex justify-center mt-5 px-4 py-2 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-secondary hover:bg-secondary-dark hover:text-white hover:no-underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
            onClick={() => history.push('/')}
          >
            Return to Newslit
          </button>
        </>
      );
    }

    if (alreadyUnsubscribedStage()) {
      return (
        <>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Already Unsubscribe</h2>
          <p className="mt-2 text-sm text-gray-600">You're already unsubscribed from this Brief.</p>
          <button
            type="button"
            className="order-0 w-full flex justify-center mt-5 px-4 py-2 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-secondary hover:bg-secondary-dark hover:text-white hover:no-underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
            onClick={() => history.push('/')}
          >
            Return to Newslit
          </button>
        </>
      );
    }
  };

  return (
    <DefaultPageLayout title="Unsubscribe from Brief">
      <div className="min-h-screen bg-white flex">
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <img className="h-12 w-auto" src={newslitLogo} alt="Newslit" />
            {currentTemplateStage()}
          </div>
        </div>
        <div className="hidden lg:block relative w-0 flex-1">
          <img
            className="absolute inset-0 h-full w-full object-cover"
            src={rocketIllustration}
            alt="Newslit Illustration"
          />
        </div>
      </div>
      <ErrorReporter />
    </DefaultPageLayout>
  );
};

export default BriefUnsubscribePage;
